// Email + domain helpers — used for matching clients across multiple email
// addresses at the same company (e.g. ahmed@uni-camel.com and
// accountant@uni-camel.com should resolve to the same client).
//
// For generic free-email domains (gmail.com, outlook.com, etc.), we fall
// back to exact-email matching — different gmail accounts aren't the same
// person.

const GENERIC_DOMAINS = new Set([
  "gmail.com", "googlemail.com",
  "outlook.com", "hotmail.com", "live.com", "msn.com",
  "yahoo.com", "yahoo.co.uk", "yahoo.fr", "yahoo.es", "yahoo.it",
  "icloud.com", "me.com", "mac.com",
  "aol.com",
  "protonmail.com", "proton.me",
  "mail.com", "gmx.com",
  "yandex.com", "yandex.ru",
  "zoho.com",
  "fastmail.com",
  "qq.com", "163.com", "126.com",
]);

export const normEmail = (e) => (e || "").trim().toLowerCase();

export function extractDomain(email) {
  const e = normEmail(email);
  const at = e.lastIndexOf("@");
  if (at < 0) return null;
  return e.slice(at + 1);
}

export function isGenericDomain(domain) {
  if (!domain) return true;
  return GENERIC_DOMAINS.has(domain.toLowerCase());
}

// Find a matching client given an email address.
// 1. Try exact email match against client.email OR any of client.aliasEmails
//    (case-insensitive). Alias emails come from merged clients so a paid
//    Stripe charge to accounts@acme.com still resolves to the surviving
//    "Acme Tours" client whose primary email is john@acme.com.
// 2. If the email's domain is a real company domain (not gmail etc.), try
//    to match any client whose email or alias is on the SAME domain
// Returns the matching client, or null.
export function findClientByEmail(email, clients) {
  const e = normEmail(email);
  if (!e) return null;
  const allEmailsForClient = (c) => {
    const all = [c.email, ...(Array.isArray(c.aliasEmails) ? c.aliasEmails : [])];
    return all.filter(Boolean).map(normEmail);
  };
  const exact = clients.find((c) => allEmailsForClient(c).includes(e));
  if (exact) return exact;
  const domain = extractDomain(e);
  if (!domain || isGenericDomain(domain)) return null;
  return clients.find((c) => allEmailsForClient(c).some((x) => extractDomain(x) === domain)) || null;
}

// Group emails by client-equivalence (same company domain when non-generic).
// Returns a Map<key, [emails]> where key is the domain for company emails
// or the email itself for generic-domain emails.
export function clientKeyForEmail(email) {
  const e = normEmail(email);
  if (!e) return null;
  const domain = extractDomain(e);
  if (!domain || isGenericDomain(domain)) return e;
  return `@${domain}`;
}
